// The inspection's Aircraft List (+): the shared components/LinkAircraftDialog
// with the inspection's write and pre-fill.
//
// Peak, Aaron 2026-10-06 (inspection 2554): "needs a (+) icon need to be able to
// link other aircraft same as config".
//
// Legacy: the tab's (+) runs add_limitation('aircraft', …) -> the limitation
// editor, "You are linking Inspection: <title>", one airframe at a time, its
// Based On / Period / Warning / Warning Unit pre-filled from the inspection
// (aircraft_templates_inspection_update.inc:211-221). Here several airframes
// take the same limitation in one write (POST /v6/inspection/aircraft-list/{id}).
// What the form shows is what is written: a field the user clears is sent null.
// Based On and Period are required (Aaron 2026-10-06).
//
// Shared by both hosts of an inspection's Aircraft List: the inspection record
// (src/inspections/InspectionComponent) and the Aircraft Templates shell
// (tabs/InspectionsTRLimitations). No Last Done / Next Due: linking records no
// maintenance.
import React, { useCallback, useMemo } from "react";

import SharedLinkAircraftDialog from "../../components/LinkAircraftDialog";
import type { LinkOutcome } from "../../components/LinkDialogFrame";
import {
  formValue,
  limitationBody,
  type BasedOnChoice,
  type LimitationFieldValues,
} from "../../components/LimitationFields";
import { getInspection, linkInspectionAircraft } from "../inspections_tr";

interface Props {
  open: boolean;
  inspectionId: number;
  /** Airframes already on the Aircraft List — not offered. */
  linkedAircraftIds: number[];
  basedOnOptions: BasedOnChoice[];
  onClose: () => void;
  onLinked: (outcome: LinkOutcome) => void;
}

const LinkAircraftDialog: React.FC<Props> = ({
  open,
  inspectionId,
  linkedAircraftIds,
  basedOnOptions,
  onClose,
  onLinked,
}) => {
  const loadDefaults = useCallback(async (): Promise<LimitationFieldValues> => {
    const i = (await getInspection(inspectionId)).data;
    return {
      based_on: formValue(i.based_on),
      period: formValue(i.period),
      warning: formValue(i.warning),
      warning_unit: formValue(i.warning_unit),
      limitation_notes: "",
    };
  }, [inspectionId]);
  const limitation = useMemo(() => ({ loadDefaults, basedOnOptions }), [loadDefaults, basedOnOptions]);

  const submit = useCallback(
    async (aircraftIds: number[], form: LimitationFieldValues | null): Promise<LinkOutcome> => {
      const result = await linkInspectionAircraft(inspectionId, {
        aircraft_ids: aircraftIds,
        ...limitationBody(form as LimitationFieldValues),
      });
      return {
        created: result.created.length,
        skipped: result.skipped.map((s) => ({ label: s.registration ?? `#${s.aircraft_id}`, reason: s.reason })),
      };
    },
    [inspectionId]
  );

  return (
    <SharedLinkAircraftDialog
      open={open}
      linkedAircraftIds={linkedAircraftIds}
      limitation={limitation}
      submit={submit}
      onClose={onClose}
      onLinked={onLinked}
    />
  );
};

export default LinkAircraftDialog;
